import { Component } from '@verndale/core';
import { timeline } from 'motion';
import { createFocusTrap } from 'focus-trap';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import ScrollAnimation from './ScrollAnimation';
import PrimaryNavigationNew from './PrimaryNavigationNew';
import { debounce, keyCode } from '../../helpers';

class HeaderNew extends Component {
  setupDefaults() {
    const self = this;

    this.dom = {
      primaryNavigationItems: this.el.querySelectorAll(
        '.primary-navigation__trigger'
      ),
      headerInner: this.el.querySelector('.header-new__inner'),
      primaryNavigation: this.el.querySelector('.primary-navigation'),
      primaryNavigationMegaMenuWrapper: this.el.querySelector(
        '.primary-navigation__megamenu-wrapper'
      ),
      scrollableSection: this.el.querySelector(
        '.primary-navigation__megamenu--scrollable'
      ),
      closeButton: this.el.querySelector(
        '.primary-navigation__megamenu-close-button'
      ),
      primaryNavigationResource: this.el.querySelector(
        '.primary-navigation__trigger-resource-desktop'
      ),
      primaryNavigationSearch: this.el.querySelector(
        '.primary-navigation__trigger-search-desktop'
      ),
      primaryNavigationToogleMenuMobile: this.el.querySelector(
        '.primary-navigation__trigger-menu-mobile'
      ),
      primaryNavigationToogleSearchMobile: this.el.querySelector(
        '.primary-navigation__trigger-search-mobile'
      ),
      primaryNavigationMegaMenuNews: this.el.querySelector(
        '.primary-navigation__megamenu-news'
      )
    };

    this.initialWidth = window.innerWidth;
    this.mainFocusTrap = createFocusTrap(
      this.dom.primaryNavigationMegaMenuWrapper,
      {
        allowOutsideClick: () => true,
        onActivate() {
          disableBodyScroll(self.el, {
            allowTouchMove: el => {
              if (el.closest('.header-new__inner')) {
                return true;
              }
            }
          });
          self.dom.scrollableSection.style.maxBlockSize = `${
            window.innerHeight - 110
          }px`;
          self.dom.scrollableSection.style.minBlockSize = `${
            window.innerHeight - 110
          }px`;
        },
        onDeactivate() {
          clearAllBodyScrollLocks();
        }
      }
    );

    new ScrollAnimation(this.el);
    this.primaryNavigation = new PrimaryNavigationNew(this.el);
  }

  addListeners() {
    window.addEventListener(
      'resize',
      debounce(this.handleResize.bind(this), 100)
    );
    this.dom.primaryNavigationItems.forEach(t => {
      t.addEventListener('click', this.handlePrimaryItemClick.bind(this));
    });
    this.dom.closeButton.addEventListener('click', this.closeMenu.bind(this));
    window.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.dom.primaryNavigationResource.addEventListener(
      'click',
      this.handlePrimaryResourceItemClick.bind(this)
    );
    this.dom.primaryNavigationToogleMenuMobile.addEventListener(
      'click',
      this.handleMenuMobileToggleClick.bind(this)
    );
    this.dom.primaryNavigationSearch.addEventListener(
      'click',
      this.handlePrimarySearchItemClick.bind(this)
    );
    this.dom.primaryNavigationToogleSearchMobile.addEventListener(
      'click',
      this.handleMobileSearchItemClick.bind(this)
    );

    // hitting the escape key will close the menu
    window.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  openMenu(isMobile, type) {
    this.prepareMegamenuForOpening();
    this.primaryNavigation.openMenu();
    
    if (this.primaryNavigation.hasMegamenuExpanded) {
      if (type === 'primary') {
        if (this.mainFocusTrap) {
          this.mainFocusTrap.activate();
        }
      }
      if (type === 'resource') {
        if (this.mainFocusTrap) {
          this.mainFocusTrap.activate();
        }
      }
      if (type === 'search') {
        if (this.mainFocusTrap) {
          this.mainFocusTrap.activate();
          this.primaryNavigation.dom.searchPanel.querySelector('.standalone-searchbox__input').value = '';
          // set focus to the search input 
          this.primaryNavigation.dom.searchPanel.querySelector('.standalone-searchbox__input').focus();
        }
      }

      
    }

    this.primaryNavigation.getWrapper().style.opacity = 1;
    return timeline(this.getOpenMenuSequence());
  }

  closeMenu() {
    if (this.mainFocusTrap) {
      this.mainFocusTrap.deactivate();
    }

    if (this.dom.primaryNavigationResource.classList.contains('active')) {
      this.dom.primaryNavigationResource.classList.remove('active');
    }

    return timeline(this.getCloseMenuSequence()).finished.then(() => {
      // this.primaryNavigation.toggleMenu();
      this.primaryNavigation.closeMenu();
    });
  }

  prepareMegamenuForOpening() {
    if (this.mainFocusTrap) {
      this.mainFocusTrap.deactivate();
    }

     // this.primaryNavigation.toggleMenu();
  }

  getOpenMenuSequence() {
    const targetHeight = getComputedStyle(
      this.primaryNavigation.getWrapper()
    ).getPropertyValue('--min-block-size-expanded');

    this.primaryNavigation.getWrapper().style.maxHeight = targetHeight;

    this.openMenuSequence = [];
    return this.openMenuSequence;
  }

  getCloseMenuSequence() {
    return [];
  }

  handlePrimaryItemClick(e) {
    if (this.primaryNavigation.hasMegamenuExpanded) {
      if (this.dom.primaryNavigationResource.classList.contains('active')) {
        this.dom.primaryNavigationResource.classList.remove('active');
      }
    }
    this.prepareMegamenuForPrimaryDesktop();

    const correspondingPrimaryTrigger = [
      ...this.primaryNavigation.dom.megamenuPrimaryTriggers
    ].find(
      t =>
        t.getAttribute('aria-controls') ===
        e.target.getAttribute('aria-controls')
    );
    if (correspondingPrimaryTrigger)
      this.primaryNavigation.expandPanelWrapper(correspondingPrimaryTrigger);
    this.openMenu(false, 'primary');
  }

  handleKeyDown(e) {
    if (
      e.keyCode === keyCode.ESC &&
      this.primaryNavigation.hasMegamenuExpanded
    ) {
      e.preventDefault();
      this.closeMenu();
    }
  }

  // handle mobile, tablet and desktop breakpoint reset
  handleResize() {
    if (this.initialWidth !== window.innerWidth) {
      if (this.primaryNavigation.hasMegamenuExpanded) {
        if (this.primaryNavigation.getIsSearchActive()) {
          this.closeSearch();
        } else {
          this.closeMenu();
        }
      }

      this.initialWidth = window.innerWidth;
    }
  }

  prepareMegamenuForResourcesDesktop() {
    // Enable news
    this.dom.primaryNavigationMegaMenuNews.classList.remove('disabled');

    // Disable primary navigation items except resource
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      if (t.classList.contains('primary-navigation__megamenu-item--resource')) {
        t.classList.remove('disabled');
      } else {
        t.classList.add('disabled');
      }
    });

    // Disable utility links wrapper
    this.primaryNavigation.dom.utilityLinksWrapper.classList.add('disabled');

    // Disable social links wrapper
    this.primaryNavigation.dom.socialLinksWrapper.classList.add('disabled');

    // Disable search panel
    this.primaryNavigation.dom.searchPanel.classList.add('disabled');
  }

  prepareMegamenuForPrimaryDesktop() {
    // Enable primary navigation items
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      t.classList.remove('disabled');
    });

    // Enable utility links wrapper
    this.primaryNavigation.dom.utilityLinksWrapper.classList.remove('disabled');

    // Enable social links wrapper
    this.primaryNavigation.dom.socialLinksWrapper.classList.remove('disabled');

    // Disable resources-for primary navigation item
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      if (
        t.classList.contains('primary-navigation__megamenu-item--resource-for')
      ) {
        t.classList.add('disabled');
      }
    });

    // Disable news
    this.dom.primaryNavigationMegaMenuNews.classList.add('disabled');

    // Disable resource primary navigation items
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      if (t.classList.contains('primary-navigation__megamenu-item--resource')) {
        t.classList.add('disabled');
      }
    });

    // Disable search panel
    this.primaryNavigation.dom.searchPanel.classList.add('disabled');
  }

  prepareMegamenuForAllMobile() {
    // Enable all primary navigation items
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      t.classList.remove('disabled');
    });
    // Disable resource primary navigation items
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      if (t.classList.contains('primary-navigation__megamenu-item--resource')) {
        t.classList.add('disabled');
      }
    });
    // Disable search panel
    this.primaryNavigation.dom.searchPanel.classList.add('disabled');
  }

  prepareMegamenuForSearch() {
    // enable search panel
    this.primaryNavigation.dom.searchPanel.classList.remove('disabled');

    // Disable primary navigation items except resource
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      if (t.classList.contains('primary-navigation__megamenu-item--resource')) {
        t.classList.remove('disabled');
      } else {
        t.classList.add('disabled');
      }
    });

    // Disable utility links wrapper
    this.primaryNavigation.dom.utilityLinksWrapper.classList.add('disabled');

    // Disable social links wrapper
    this.primaryNavigation.dom.socialLinksWrapper.classList.add('disabled');

    // Disable resources-for primary navigation item
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      if (
        t.classList.contains('primary-navigation__megamenu-item--resource-for')
      ) {
        t.classList.add('disabled');
      }
    });

    // Disable news
    this.dom.primaryNavigationMegaMenuNews.classList.add('disabled');

    // Disable resource primary navigation items
    this.primaryNavigation.dom.megamenuPrimaryItems.forEach(t => {
      if (t.classList.contains('primary-navigation__megamenu-item--resource')) {
        t.classList.add('disabled');
      }
    });
  }

  toggleResourcesDesktopMenu () {
    if (this.dom.primaryNavigationResource.classList.contains('active')) {
        this.closeMenu();
    } else {
      this.dom.primaryNavigationResource.classList.add('active');
    }
  }

  handlePrimaryResourceItemClick() {
    
    this.toggleResourcesDesktopMenu();
    this.prepareMegamenuForResourcesDesktop();
    this.openMenu(false, 'resource');
  }

  handlePrimarySearchItemClick(e) {
    this.prepareMegamenuForSearch();

    if (this.primaryNavigation.hasMegamenuExpanded) {
      if (e.currentTarget.classList.contains('active')) {
        e.currentTarget.classList.remove('active');
      }
    } else {
      e.currentTarget.classList.add('active');
    }

    this.openMenu(false, 'search');
  }

  toggleSearchMobile () {

    if (!this.primaryNavigation.hasMegamenuExpanded) {
      this.dom.primaryNavigationToogleSearchMobile.querySelector('.search-toggle').style.display = 'none';
      this.dom.primaryNavigationToogleSearchMobile.querySelector('.close').style.display = 'flex';
      this.dom.primaryNavigationToogleSearchMobile.classList.add('opened');
      this.openMenu(true, 'search');
    } else {
      this.dom.primaryNavigationToogleSearchMobile.querySelector('.search-toggle').style.display = 'flex';
      this.dom.primaryNavigationToogleSearchMobile.querySelector('.close').style.display = 'none';
      this.dom.primaryNavigationToogleSearchMobile.classList.remove('opened');
      this.closeMenu();
    }
  }

  handleMobileSearchItemClick() {
    this.prepareMegamenuForSearch();


    if (this.dom.primaryNavigationToogleMenuMobile.classList.contains('opened')) {
      this.toggleMenuMobile();
      this.toggleSearchMobile();
    }

    this.toggleSearchMobile();
  }

  toggleMenuMobile () {
    if (!this.primaryNavigation.hasMegamenuExpanded) {
      this.dom.primaryNavigationToogleMenuMobile.querySelector('.menu').style.display = 'none';
      this.dom.primaryNavigationToogleMenuMobile.querySelector('.close').style.display = 'flex';
      this.dom.primaryNavigationToogleMenuMobile.classList.add('opened');
      this.openMenu(true, 'menu');
    } else {
      this.dom.primaryNavigationToogleMenuMobile.querySelector('.menu').style.display = 'flex';
      this.dom.primaryNavigationToogleMenuMobile.querySelector('.close').style.display = 'none';
      this.dom.primaryNavigationToogleMenuMobile.classList.remove('opened');
      this.closeMenu();
    }
  }

  handleMenuMobileToggleClick() {
    this.prepareMegamenuForAllMobile();

    if (this.dom.primaryNavigationToogleSearchMobile.classList.contains('opened')) {
      this.toggleSearchMobile();
      this.toggleMenuMobile();
    }

    this.toggleMenuMobile();
  }
}

export default HeaderNew;
